import { commonAction } from '../types/commonActions';
import { actionTypes } from './actionTypes';
import { createContext } from './createContext';
import { CouponInfo, Discounts, PaymentSummary } from '../types/cart';

export type paymentDataType = {
  isTCChecked: boolean;
  couponsApplied: CouponInfo[];
  assistantCode: string;
  paymentSummary: PaymentSummary;
  isAssistantCodeValid: boolean;
  ncemi?: {
    label: string;
    isAllowed: boolean;
  };
  bnpl?: {
    label: string;
    isAllowed: boolean;
  };
  bankCashback?: {
    label: string;
    isAllowed: boolean;
  };
};

export type paymentDispatchType = {
  setT_CStatus: Function;
  setCartCouponsApplied: (couponsApplied: CouponInfo[]) => {};
  setPaymentSummary: Function;
  setAssistantCode: Function;
  setIsAssistantCodeValid: (isAssistantCodeValid: boolean) => {};
  setNcemi: (ncemi?: { label: string; isAllowed: boolean }) => {};
  setBankCashback: (bankCashback?: { label: string; isAllowed: boolean }) => {};
  setBnpl: (bnpl?: { label: string; isAllowed: boolean }) => {};
};

const initialState: paymentDataType = {
  isTCChecked: true,
  couponsApplied: [],
  paymentSummary: {
    actualPrice: 0,
    discount: 0,
    couponDiscount: 0,
    finalPrice: 0,
    discounts: [],
  },
  assistantCode: '',
  isAssistantCodeValid: true,
  bnpl: {
    label: '',
    isAllowed: false,
  },
  bankCashback: {
    label: '',
    isAllowed: false,
  },
  ncemi: {
    label: '',
    isAllowed: false,
  },
};
function paymentReducer(state: paymentDataType, action: commonAction) {
  const {
    isTCChecked = initialState.isTCChecked,
    couponsApplied = initialState.couponsApplied,
    paymentSummary = initialState.paymentSummary,
    assistantCode = initialState.assistantCode,
    isAssistantCodeValid = initialState.isAssistantCodeValid,
    ncemi = initialState.ncemi,
    bankCashback = initialState.bankCashback,
    bnpl = initialState.bnpl,
  }: paymentDataType = action.payload;
  switch (action.type) {
    case actionTypes.ENABLE_PURCHASE_BUTTON: {
      return {
        ...state,
        isTCChecked: isTCChecked,
      };
    }
    case actionTypes.SET_CART_COUPONS_APPLIED: {
      return {
        ...state,
        couponsApplied: couponsApplied,
      };
    }
    case actionTypes.SET_PAYMENT_SUMMARY: {
      return {
        ...state,
        paymentSummary: paymentSummary,
      };
    }
    case actionTypes.SET_ASSISTANT_CODE: {
      return {
        ...state,
        assistantCode: assistantCode,
      };
    }
    case actionTypes.SET_IS_ASSISTANT_CODE_VALID: {
      return {
        ...state,
        isAssistantCodeValid: isAssistantCodeValid,
      };
    }
    case actionTypes.SET_NCEMI_PROMOTION: {
      return {
        ...state,
        ncemi: ncemi,
      };
    }
    case actionTypes.SET_BANK_CASHBACK_PROMOTION: {
      return {
        ...state,
        bankCashback: bankCashback,
      };
    }
    case actionTypes.SET_BNPL_PROMOTION: {
      return {
        ...state,
        bnpl: bnpl,
      };
    }
    // istanbul ignore next
    default:
      throw new Error(`Unsupported type: ${action.type}`);
  }
}

const setT_CStatus = (dispatch: any) => (isTCChecked: boolean) => {
  dispatch({ type: actionTypes.ENABLE_PURCHASE_BUTTON, payload: { isTCChecked: isTCChecked } });
};

const setCartCouponsApplied = (dispatch: any) => (couponsApplied: CouponInfo[]) => {
  dispatch({
    type: actionTypes.SET_CART_COUPONS_APPLIED,
    payload: { couponsApplied: couponsApplied },
  });
};

const setPaymentSummary =
  (dispatch: any) =>
  (paymentSummary: {
    actualPrice: number;
    discount: number;
    couponDiscount: number;
    finalPrice: number;
    discounts: Discounts[];
    bnpl?: {
      label: string;
      isAllowed: boolean;
    };
    cashback?: {
      label: string;
      isAllowed: boolean;
    };
    ncemi?: {
      label: string;
      isAllowed: boolean;
    };
  }) => {
    dispatch({
      type: actionTypes.SET_PAYMENT_SUMMARY,
      payload: { paymentSummary: paymentSummary },
    });
  };

const setAssistantCode = (dispatch: any) => (assistantCode: string) => {
  dispatch({
    type: actionTypes.SET_ASSISTANT_CODE,
    payload: { assistantCode: assistantCode },
  });
};

const setIsAssistantCodeValid = (dispatch: any) => (isAssistantCodeValid: boolean) => {
  dispatch({
    type: actionTypes.SET_IS_ASSISTANT_CODE_VALID,
    payload: { isAssistantCodeValid: isAssistantCodeValid },
  });
};

const setNcemi = (dispatch: any) => (ncemi?: { label: string; isAllowed: boolean }) => {
  dispatch({
    type: actionTypes.SET_NCEMI_PROMOTION,
    payload: { ncemi: ncemi || initialState.ncemi },
  });
};

const setBankCashback =
  (dispatch: any) => (bankCashback?: { label: string; isAllowed: boolean }) => {
    dispatch({
      type: actionTypes.SET_BANK_CASHBACK_PROMOTION,
      payload: { bankCashback: bankCashback || initialState.bankCashback },
    });
  };

const setBnpl = (dispatch: any) => (bnpl?: { label: string; isAllowed: boolean }) => {
  dispatch({
    type: actionTypes.SET_BNPL_PROMOTION,
    payload: { bnpl: bnpl || initialState.bnpl },
  });
};

const { Context: PaymentContext, Provider: PaymentProvider } = createContext<
  paymentDataType,
  paymentDispatchType
>(
  paymentReducer,
  {
    setT_CStatus,
    setCartCouponsApplied,
    setPaymentSummary,
    setAssistantCode,
    setIsAssistantCodeValid,
    setNcemi,
    setBankCashback,
    setBnpl,
  },
  initialState,
);

PaymentContext.displayName = 'Payment';

export { PaymentContext, PaymentProvider };
